import * as React from "react";
import { Modal, Form, Input, Select, Button, Table, Typography, Divider, InputNumber } from "antd";
import { PlusOutlined, DeleteOutlined, SelectOutlined } from "@ant-design/icons";
import AppInput from "src/components/common/AppInput";
import {
  getRewardApi,
  createRewardApi,
  updateRewardApi,
  type RewardListItem,
} from "src/api/reward/rewardApi";
import type { RewardItemInput, RewardType } from "src/types/reward";
import type { CouponListItem } from "src/api/coupon/couponApi";
import { snackbarStore } from "src/store/snackbarStore";
import CouponSelectModal from "./CouponSelectModal";

type Props = {
  open: boolean;
  editingItem: RewardListItem | null;
  onClose: () => void;
  onSuccess: () => void;
};

// 폼에서만 쓰는 표시용 필드(couponName)를 덧붙인 항목
type EditableRewardItem = RewardItemInput & { couponName?: string };

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

const makeDefaultItem = (displayOrder: number): EditableRewardItem => ({
  rewardType: "POINT",
  pointAmount: 0,
  pointExpiryDays: 365,
  quantity: 1,
  displayOrder,
});

export default function RewardUpsertModal({ open, editingItem, onClose, onSuccess }: Props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [items, setItems] = React.useState<EditableRewardItem[]>([]);
  const [couponSelectModalOpen, setCouponSelectModalOpen] = React.useState(false);
  const [selectingIndex, setSelectingIndex] = React.useState<number | null>(null);

  const isEdit = !!editingItem;

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setItems([]);
      return;
    }

    if (isEdit) {
      const fetchDetail = async () => {
        try {
          setLoading(true);
          const res = await getRewardApi(editingItem.rewardId);
          if (res.statusCode !== 1 || !res.data) {
            toast(res.message || "리워드 정보를 불러올 수 없습니다.", "error");
            onClose();
            return;
          }

          const data = res.data;
          form.setFieldsValue({
            rewardName: data.rewardName,
            rewardCode: data.rewardCode,
            description: data.description,
            imageUrl: data.imageUrl,
          });
          setItems(
            (data.items || []).map((item, index) => ({
              itemId: item.itemId,
              rewardType: item.rewardType,
              pointAmount: item.pointAmount,
              pointExpiryDays: item.pointExpiryDays,
              couponId: item.couponId,
              couponName: item.couponName,
              quantity: item.quantity ?? 1,
              displayOrder: item.displayOrder ?? index,
            }))
          );
        } catch (e: any) {
          toast(e?.message || "리워드 정보를 불러오는 중 오류가 발생했습니다.", "error");
          onClose();
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
    } else {
      form.resetFields();
      setItems([makeDefaultItem(0)]);
    }
  }, [open, isEdit, editingItem, form, onClose]);

  const handleAddItem = () => {
    setItems((prev) => [...prev, makeDefaultItem(prev.length)]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof EditableRewardItem, value: any) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        if (field === "rewardType") {
          if (value === "POINT") {
            updated.couponId = undefined;
            updated.couponName = undefined;
            updated.pointAmount = updated.pointAmount ?? 0;
            updated.pointExpiryDays = updated.pointExpiryDays ?? 365;
          } else {
            updated.pointAmount = undefined;
            updated.pointExpiryDays = undefined;
          }
        }
        return updated;
      })
    );
  };

  const handleOpenCouponModal = (index: number) => {
    setSelectingIndex(index);
    setCouponSelectModalOpen(true);
  };

  const handleCouponSelect = (coupon: CouponListItem) => {
    if (selectingIndex === null) return;
    setItems((prev) =>
      prev.map((item, i) =>
        i === selectingIndex
          ? { ...item, couponId: coupon.couponId, couponName: coupon.couponName }
          : item
      )
    );
    setSelectingIndex(null);
    setCouponSelectModalOpen(false);
  };

  const handleSubmit = async (values: any) => {
    if (items.length === 0) {
      toast("보상 항목을 1개 이상 추가하세요.", "warning");
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.rewardType === "POINT" && !item.pointAmount && item.pointAmount !== 0) {
        toast(`항목 ${i + 1}: 포인트 금액을 입력하세요.`, "warning");
        return;
      }
      if (item.rewardType === "COUPON" && !item.couponId) {
        toast(`항목 ${i + 1}: 쿠폰을 선택하세요.`, "warning");
        return;
      }
    }

    try {
      setSubmitting(true);

      const payloadItems = items.map(({ couponName, ...item }, index) => ({
        ...(item.itemId ? { itemId: item.itemId } : {}),
        rewardType: item.rewardType,
        ...(item.rewardType === "POINT"
          ? {
              pointAmount: item.pointAmount ?? 0,
              pointExpiryDays: item.pointExpiryDays ?? undefined,
            }
          : { couponId: item.couponId }),
        quantity: item.quantity ?? 1,
        displayOrder: item.displayOrder ?? index,
      }));

      let res;
      if (isEdit) {
        // 수정 시 rewardCode 는 서버에서 변경을 지원하지 않습니다.
        res = await updateRewardApi(editingItem.rewardId, {
          rewardName: values.rewardName,
          description: values.description || undefined,
          imageUrl: values.imageUrl || undefined,
          items: payloadItems,
        });
      } else {
        res = await createRewardApi({
          rewardName: values.rewardName,
          rewardCode: values.rewardCode,
          description: values.description || undefined,
          imageUrl: values.imageUrl || undefined,
          items: payloadItems,
        });
      }

      if (res.statusCode === 1) {
        toast(isEdit ? "리워드가 수정되었습니다." : "리워드가 등록되었습니다.", "success");
        onSuccess();
      } else {
        toast(res?.message || `리워드 ${isEdit ? "수정" : "등록"} 실패`, "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const itemColumns = [
    {
      title: "순서",
      dataIndex: "order",
      width: 60,
      align: "center" as const,
      render: (_: any, __: EditableRewardItem, index: number) => index + 1,
    },
    {
      title: "타입 *",
      dataIndex: "rewardType",
      width: 120,
      render: (value: RewardType, _: EditableRewardItem, index: number) => (
        <Select
          value={value}
          onChange={(val: RewardType) => handleItemChange(index, "rewardType", val)}
          style={{ width: "100%" }}
          options={[
            { value: "POINT", label: "포인트" },
            { value: "COUPON", label: "쿠폰" },
          ]}
        />
      ),
    },
    {
      title: "보상 내용 *",
      dataIndex: "reward",
      width: 300,
      render: (_: any, record: EditableRewardItem, index: number) =>
        record.rewardType === "POINT" ? (
          <InputNumber
            value={record.pointAmount}
            onChange={(val) => handleItemChange(index, "pointAmount", val ?? 0)}
            style={{ width: "100%" }}
            placeholder="포인트 금액"
            min={0}
            maxLength={9}
            addonAfter="P"
          />
        ) : (
          <Button
            icon={<SelectOutlined />}
            onClick={() => handleOpenCouponModal(index)}
            style={{ width: "100%" }}
          >
            {record.couponName
              ? `${record.couponName} (ID: ${record.couponId})`
              : record.couponId
                ? `쿠폰 ID: ${record.couponId}`
                : "쿠폰 선택"}
          </Button>
        ),
    },
    {
      title: "포인트 유효기간(일)",
      dataIndex: "pointExpiryDays",
      width: 150,
      render: (value: number, record: EditableRewardItem, index: number) =>
        record.rewardType === "POINT" ? (
          <InputNumber
            value={value}
            onChange={(val) => handleItemChange(index, "pointExpiryDays", val ?? undefined)}
            style={{ width: "100%" }}
            placeholder="선택사항"
            min={0}
          />
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
    {
      title: "수량",
      dataIndex: "quantity",
      width: 90,
      render: (value: number, _: EditableRewardItem, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => handleItemChange(index, "quantity", val ?? 1)}
          style={{ width: "100%" }}
          min={1}
        />
      ),
    },
    {
      title: "표시 순서",
      dataIndex: "displayOrder",
      width: 100,
      render: (value: number, _: EditableRewardItem, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => handleItemChange(index, "displayOrder", val ?? 0)}
          style={{ width: "100%" }}
          min={0}
        />
      ),
    },
    {
      title: "관리",
      dataIndex: "actions",
      width: 90,
      align: "center" as const,
      fixed: "right" as const,
      render: (_: any, __: EditableRewardItem, index: number) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
        >
          삭제
        </Button>
      ),
    },
  ];

  return (
    <>
      <CouponSelectModal
        open={couponSelectModalOpen}
        onClose={() => {
          setCouponSelectModalOpen(false);
          setSelectingIndex(null);
        }}
        onSelect={handleCouponSelect}
      />
      <Modal
        title={isEdit ? "리워드 수정" : "리워드 등록"}
        open={open}
        onCancel={onClose}
        maskClosable={false}
        width={1100}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto", padding: 24 } }}
        footer={[
          <Button key="cancel" onClick={onClose} disabled={submitting}>
            취소
          </Button>,
          <Button key="submit" type="primary" loading={submitting} onClick={() => form.submit()}>
            {isEdit ? "수정" : "등록"}
          </Button>,
        ]}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>로딩 중...</div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Typography.Title level={5}>기본 정보</Typography.Title>

            <Form.Item
              label="리워드명"
              name="rewardName"
              rules={[{ required: true, message: "리워드명을 입력하세요" }]}
            >
              <AppInput placeholder="리워드명 입력" />
            </Form.Item>

            <Form.Item
              label="리워드 코드"
              name="rewardCode"
              rules={[{ required: !isEdit, message: "리워드 코드를 입력하세요" }]}
              extra={isEdit ? "리워드 코드는 수정할 수 없습니다." : undefined}
            >
              <AppInput placeholder="리워드 코드 입력 (예: SIGNUP_POINT)" disabled={isEdit} />
            </Form.Item>

            <Form.Item label="설명" name="description">
              <Input.TextArea rows={2} placeholder="리워드 설명 입력" />
            </Form.Item>

            <Form.Item label="이미지 URL" name="imageUrl">
              <AppInput placeholder="이미지 URL 입력 (선택)" />
            </Form.Item>

            <Divider />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <div>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  보상 항목
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  1개면 단일 리워드, 2개 이상이면 쿠폰팩으로 동작합니다.
                </Typography.Text>
              </div>
              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddItem}>
                항목 추가
              </Button>
            </div>

            {items.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                보상 항목을 추가하세요
              </div>
            ) : (
              <Table
                columns={itemColumns}
                dataSource={items}
                rowKey={(_, index) => String(index)}
                pagination={false}
                size="small"
                scroll={{ x: 1000 }}
              />
            )}
          </Form>
        )}
      </Modal>
    </>
  );
}
